import { buildAudioSnapshot, classifyInput } from './audio-devices';
import type { InputKind, RawDevice } from './audio-devices';

// One sample window of the live take. `t` is ms since recording started, so the
// two beats are sliced by the timeline that was imposed on the SDR rather than
// by trying to detect where speech stopped.
export interface MicFrame {
  t: number;
  rms: number; // 0..1 linear
  peak: number; // 0..1 linear, max |sample| in the window
  clipped: number; // samples at or above CLIP_LEVEL in this window
  samples: number; // window length in samples
}

export interface TakeTimeline {
  speakMs: number;
  // Where the take actually ended, which is the end of the quiet window — a take
  // stopped early has no quiet beat at all.
  stoppedAtMs: number;
}

export interface TakeMetrics {
  durationMs: number;
  // The largest gap between consecutive samples. A frames-per-second ratio would
  // measure the SDR's display refresh (120Hz laptops, 50Hz panels) as much as the
  // take; a gap is the same size on every screen.
  maxGapMs: number;
  speechDb: number | null; // p90 of the speaking beat
  noiseDb: number | null; // p50 of the quiet beat — its floor
  quietPeakDb: number | null; // p90 of the quiet beat — its peaks
  snrDb: number | null;
  clippedFraction: number;
  peakDb: number;
  quietContaminated: boolean;
  tooShort: boolean;
}

export type Verdict = 'ok' | 'bad' | 'unknown';
export type VerdictId = 'captured' | 'clipping' | 'noise' | 'device';

export interface VerdictRow {
  id: VerdictId;
  verdict: Verdict;
  title: string;
  body: string;
  // Set only where a better mic is attached and resolvable, so the row can offer
  // the switch instead of describing it.
  switchToDeviceId?: string;
}

export type SdrVerdict = 'ok' | 'retry' | 'closed';

// What the browser actually applied to the capture. Recorded because every level
// below describes the PROCESSED stream: with noise suppression on, the quiet beat
// is gated toward silence, so the noise numbers describe the suppressor as much
// as the room. Without these the corpus cannot be read back.
export interface CaptureSettings {
  echoCancellation: boolean | null;
  // Which canceller ran, where the engine says. Native cancellation on Windows is
  // the documented wildcard, and this is the field that tells the two apart.
  echoCancellationType: string | null;
  noiseSuppression: boolean | null;
  autoGainControl: boolean | null;
  sampleRate: number | null;
  channelCount: number | null;
  mimeType: string | null;
}

// Engines disagree on these: at least one reports a mode string where the type
// says flag, so anything that isn't a plain on/off is recorded as unknown.
export const asBool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
export const asText = (v: unknown): string | null => (typeof v === 'string' ? v : null);

export const SPEAK_MS = 6_000;
export const QUIET_MS = 2_000;
// Dropped at the start (the SDR's reaction time to "speak now") and either side of
// the beat boundary (timer skew, and the tail of the last word).
export const GUARD_MS = 300;
export const MIN_TAKE_MS = 3_000;
export const CLIP_LEVEL = 0.99;
export const MAX_GAP_MS = 250;
export const DB_FLOOR = -100;
const CLIP_OK = 0.0005;
const CLIP_BAD = 0.01;
const SNR_OK_DB = 20;
const SNR_BAD_DB = 10;
const SILENCE_PEAK_DB = -60;
const QUIET_CONTAM_DB = 6;

// Stamped on every logged take. The prompt is deliberately loose ("say your usual
// opener"), so takes are not comparable across SDRs — this makes a later change to
// the recording protocol visible in the data instead of silently mixing two
// populations into one distribution.
export const PROTOCOL = 'own_opener_6s_2s_v1';

// Floors instead of returning -Infinity: a digitally silent window would otherwise
// poison every percentile it takes part in.
export function toDb(amplitude: number): number {
  if (!(amplitude > 0)) return DB_FLOOR;
  return Math.max(DB_FLOOR, 20 * Math.log10(amplitude));
}

export function percentile(values: readonly number[], p: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * p)));
  return sorted[idx] ?? null;
}

const maxGap = (frames: readonly MicFrame[], durationMs: number): number => {
  // Anchored at both ends, so a take that stopped producing samples partway
  // through is caught as well as one that stuttered in the middle.
  let prev = 0;
  let worst = 0;
  for (const f of frames) {
    worst = Math.max(worst, f.t - prev);
    prev = f.t;
  }
  return Math.max(worst, durationMs - prev);
};

export function summariseTake(frames: readonly MicFrame[], timeline: TakeTimeline): TakeMetrics {
  const { speakMs, stoppedAtMs } = timeline;
  const inSpeech = frames.filter((f) => f.t >= GUARD_MS && f.t <= speakMs - GUARD_MS);
  const inQuiet = frames.filter((f) => f.t >= speakMs + GUARD_MS && f.t <= stoppedAtMs);

  const speechDb = percentile(
    inSpeech.map((f) => toDb(f.rms)),
    0.9,
  );
  const quietDbs = inQuiet.map((f) => toDb(f.rms));
  const noiseDb = percentile(quietDbs, 0.5);
  const quietPeakDb = percentile(quietDbs, 0.9);

  let clipped = 0;
  let samples = 0;
  let peak = 0;
  for (const f of frames) {
    clipped += f.clipped;
    samples += f.samples;
    peak = Math.max(peak, f.peak);
  }

  return {
    durationMs: stoppedAtMs,
    maxGapMs: maxGap(frames, stoppedAtMs),
    speechDb,
    noiseDb,
    quietPeakDb,
    snrDb: speechDb !== null && noiseDb !== null ? speechDb - noiseDb : null,
    clippedFraction: samples > 0 ? clipped / samples : 0,
    peakDb: toDb(peak),
    // A SHAPE test, deliberately not the SNR test. `noiseDb` is the quiet beat's
    // floor; this compares its PEAKS against the speaking beat, which is what
    // separates "kept talking" from "sits in a noisy room". Deriving both from the
    // same comparison would make one of them unreachable.
    quietContaminated:
      speechDb !== null && quietPeakDb !== null && quietPeakDb >= speechDb - QUIET_CONTAM_DB,
    tooShort: stoppedAtMs < MIN_TAKE_MS,
  };
}

// A take we did not fully observe cannot carry a level-based claim. Kept separate
// from `tooShort` so the two can say different things to the SDR.
const unobserved = (m: TakeMetrics): boolean => m.maxGapMs > MAX_GAP_MS;

const unclearRow = (id: VerdictId, title: string): VerdictRow => ({
  id,
  verdict: 'unknown',
  title,
  body: 'We didn’t get a clean look at the whole recording. Try again with this tab in front.',
});

const tooShortRow = (id: VerdictId): VerdictRow => ({
  id,
  verdict: 'unknown',
  title: 'That was too short to tell',
  body: 'Record again and let it run to the end.',
});

function capturedRow(m: TakeMetrics): VerdictRow {
  // Both guards come before the silence test, and that order is the whole point:
  // an unobserved take has no samples, which looks exactly like a dead mic. A
  // short or unwatched recording is not evidence of anything.
  if (m.tooShort) return tooShortRow('captured');
  if (unobserved(m)) return unclearRow('captured', 'Couldn’t check that recording');
  if (m.peakDb < SILENCE_PEAK_DB) {
    return {
      id: 'captured',
      verdict: 'bad',
      title: 'Your mic sent no sound at all',
      body: 'Check it isn’t muted — some headsets have a switch on the cable — and that the right mic is picked in the audio panel.',
    };
  }
  if (m.speechDb === null) {
    return {
      id: 'captured',
      verdict: 'unknown',
      title: 'We didn’t hear you speak',
      body: 'There was sound, but nothing that looked like speech. Try again and say a full sentence.',
    };
  }
  return {
    id: 'captured',
    verdict: 'ok',
    title: 'We heard you',
    body: 'Your mic is picking up your voice.',
  };
}

function clippingRow(m: TakeMetrics): VerdictRow {
  if (m.tooShort) return tooShortRow('clipping');
  if (unobserved(m)) return unclearRow('clipping', 'Couldn’t check for distortion');
  if (m.clippedFraction > CLIP_BAD) {
    return {
      id: 'clipping',
      verdict: 'bad',
      title: 'Your mic is overloading',
      body: 'The loudest parts are being cut off, which is what makes a voice sound harsh or crackly. Move the mic further from your mouth — and on Windows, check Microphone Boost isn’t set to +10 or +20 dB in Sound settings.',
    };
  }
  if (m.clippedFraction < CLIP_OK) {
    return {
      id: 'clipping',
      verdict: 'ok',
      title: 'No distortion',
      body: 'Nothing is being cut off at the loud end.',
    };
  }
  return {
    id: 'clipping',
    verdict: 'unknown',
    title: 'A little distortion',
    body: 'Not enough to call it either way — trust the playback above.',
  };
}

function noiseRow(m: TakeMetrics): VerdictRow {
  if (m.tooShort) return tooShortRow('noise');
  if (unobserved(m)) return unclearRow('noise', 'Couldn’t check your background');
  if (m.quietContaminated) {
    return {
      id: 'noise',
      verdict: 'unknown',
      title: 'Couldn’t get a background reading',
      body: 'We measure your background level during the quiet couple of seconds at the end of the recording.',
    };
  }
  if (m.snrDb === null) {
    return {
      id: 'noise',
      verdict: 'unknown',
      title: 'Couldn’t hear your background',
      body: 'Let the recording run to the end, including the quiet part.',
    };
  }
  if (m.snrDb <= SNR_BAD_DB) {
    return {
      id: 'noise',
      verdict: 'bad',
      title: 'Your voice is competing with the background',
      body: 'Move the mic clear of your clothing and put it in front of your face — a mic buried against a collar picks up more of the room than of you.',
    };
  }
  if (m.snrDb >= SNR_OK_DB) {
    return {
      id: 'noise',
      verdict: 'ok',
      title: 'Your voice stands out',
      body: 'You’re clear of the background noise.',
    };
  }
  return {
    id: 'noise',
    verdict: 'unknown',
    title: 'Some background noise',
    body: 'Not enough to call it either way — trust the playback above.',
  };
}

function deviceRow(inputs: readonly RawDevice[], selectedId: string | null): VerdictRow {
  const snap = buildAudioSnapshot(inputs, selectedId);
  if (snap.resolvedLabel === null) {
    return {
      id: 'device',
      verdict: 'unknown',
      title: 'Couldn’t tell which mic that was',
      body: 'The browser didn’t name the device. Pick your mic explicitly in the audio panel.',
    };
  }
  const kind = classifyInput(snap.resolvedLabel);
  if (kind === 'external') {
    return {
      id: 'device',
      verdict: 'ok',
      title: `Recorded on “${snap.resolvedLabel}”`,
      body: 'That’s a headset or an external mic — the right kind of thing to be on.',
    };
  }
  if (kind === 'built_in' && snap.idleExternalLabel !== null) {
    const target = snap.inputs.find((d) => !d.pseudo && d.label === snap.idleExternalLabel);
    return {
      id: 'device',
      verdict: 'bad',
      title: 'You’re on the built-in microphone',
      body: `“${snap.idleExternalLabel}” is connected — prospects hear you better on it.`,
      ...(target ? { switchToDeviceId: target.deviceId } : {}),
    };
  }
  if (kind === 'built_in') {
    return {
      id: 'device',
      verdict: 'unknown',
      title: 'You’re on your laptop’s microphone',
      body: 'It works, but a headset sits closer to your mouth and picks up less of the room.',
    };
  }
  return {
    id: 'device',
    verdict: 'unknown',
    title: `Recorded on “${snap.resolvedLabel}”`,
    body: 'We can’t say whether that’s a good mic for calls. Trust the playback above.',
  };
}

export type TakeFailure = 'unmeasured' | 'bad';

// Only silence and overload gate a pass: they are physical facts, where the noise and
// device verdicts rest on thresholds not yet checked against what SDRs hear. A take
// too short or patchy to measure cannot pass, but is no evidence against the mic.
export function takeFailure(m: TakeMetrics, rows: readonly VerdictRow[]): TakeFailure | null {
  if (m.tooShort || unobserved(m)) return 'unmeasured';
  const bad = rows.some((r) => (r.id === 'captured' || r.id === 'clipping') && r.verdict === 'bad');
  return bad ? 'bad' : null;
}

// From the rendered ranges, never from `ended` alone: seeking to the end fires it
// without a sample having been heard. A blob with no duration yet counts on any
// real playback.
export function playedThrough(
  played: readonly (readonly [start: number, end: number])[],
  duration: number,
): boolean {
  const heard = played.reduce((sum, [start, end]) => sum + Math.max(0, end - start), 0);
  if (!Number.isFinite(duration)) return heard > 0;
  return duration > 0 && heard >= 0.9 * duration;
}

export function verdictsFor(
  metrics: TakeMetrics,
  inputs: readonly RawDevice[],
  selectedId: string | null,
): VerdictRow[] {
  return [
    capturedRow(metrics),
    clippingRow(metrics),
    noiseRow(metrics),
    deviceRow(inputs, selectedId),
  ];
}

// One row per take. Index signature so this satisfies the logger's context type
// directly; every field is still declared, so a typo in a key is a type error at
// construction.
export interface MicCheckLog {
  [key: string]: string | number | boolean | null;
  event: 'cockpit_mic_check';
  protocol: string;
  take: number;
  duration_ms: number;
  max_gap_ms: number;
  speech_db: number | null;
  noise_db: number | null;
  quiet_peak_db: number | null;
  snr_db: number | null;
  clipped_fraction: number;
  peak_db: number;
  quiet_contaminated: boolean;
  too_short: boolean;
  v_captured: Verdict;
  v_clipping: Verdict;
  v_noise: Verdict;
  v_device: Verdict;
  // What the SDR said, against what we measured. The disagreements are the whole
  // reason this row exists: a noise or device warning waved through is a candidate
  // false alarm, and a clean take re-recorded is a candidate miss.
  sdr_verdict: SdrVerdict;
  // Why "All good" was withheld, if it was.
  failure: TakeFailure | null;
  played_through: boolean;
  echo_cancellation: boolean | null;
  echo_cancellation_type: string | null;
  noise_suppression: boolean | null;
  auto_gain_control: boolean | null;
  sample_rate: number | null;
  channel_count: number | null;
  mime_type: string | null;
  platform: string | null;
  // Group by the LABEL. Device ids are salted per browser profile and rotate, so
  // they mean nothing once the session ends.
  resolved_label: string | null;
  resolved_kind: InputKind | null;
  idle_external_label: string | null;
  real_input_count: number;
  selected_pseudo: boolean;
}

const round = (v: number | null, dp = 1): number | null =>
  v === null ? null : Number(v.toFixed(dp));

export function buildMicCheckLog(args: {
  metrics: TakeMetrics;
  rows: readonly VerdictRow[];
  inputs: readonly RawDevice[];
  selectedId: string | null;
  capture: CaptureSettings;
  platform: string | null;
  take: number;
  sdrVerdict: SdrVerdict;
}): MicCheckLog {
  const { metrics: m, rows, capture } = args;
  const snap = buildAudioSnapshot(args.inputs, args.selectedId);
  const verdict = (id: VerdictId): Verdict => rows.find((r) => r.id === id)?.verdict ?? 'unknown';
  return {
    event: 'cockpit_mic_check',
    protocol: PROTOCOL,
    take: args.take,
    duration_ms: Math.round(m.durationMs),
    max_gap_ms: Math.round(m.maxGapMs),
    speech_db: round(m.speechDb),
    noise_db: round(m.noiseDb),
    quiet_peak_db: round(m.quietPeakDb),
    snr_db: round(m.snrDb),
    clipped_fraction: Number(m.clippedFraction.toFixed(5)),
    peak_db: Number(m.peakDb.toFixed(1)),
    quiet_contaminated: m.quietContaminated,
    too_short: m.tooShort,
    v_captured: verdict('captured'),
    v_clipping: verdict('clipping'),
    v_noise: verdict('noise'),
    v_device: verdict('device'),
    sdr_verdict: args.sdrVerdict,
    failure: takeFailure(m, rows),
    // Known only once the SDR has had the chance to listen.
    played_through: false,
    echo_cancellation: capture.echoCancellation,
    echo_cancellation_type: capture.echoCancellationType,
    noise_suppression: capture.noiseSuppression,
    auto_gain_control: capture.autoGainControl,
    sample_rate: capture.sampleRate,
    channel_count: capture.channelCount,
    mime_type: capture.mimeType,
    platform: args.platform,
    resolved_label: snap.resolvedLabel,
    resolved_kind: snap.resolvedLabel === null ? null : classifyInput(snap.resolvedLabel),
    idle_external_label: snap.idleExternalLabel,
    real_input_count: snap.realInputCount,
    selected_pseudo: snap.selectedPseudo,
  };
}

const LS_PASSED_DAY = 'maestro.micCheck.passedDay';

// The SDR's own calendar day, not UTC: they are spread across timezones, and a
// pass that expires at midnight in London is wrong for most of them.
export function todayKey(now: Date): string {
  const mm = `${now.getMonth() + 1}`.padStart(2, '0');
  const dd = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

const PASS_DAYS = 7;

// A pass lasts PASS_DAYS local calendar days, counting the day it was made. Rounded,
// because a DST change makes the span between two local midnights 23 or 25 hours; a
// pass dated after today (a clock moved back) is no pass.
export function passCurrent(stored: string | null, now: Date): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(stored ?? '');
  if (!m) return false;
  const passed = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((today.getTime() - passed.getTime()) / 86_400_000);
  return days >= 0 && days < PASS_DAYS;
}

// Where storage is blocked or full, a pass still has to open the pool picker for the
// rest of the page's life, so it is held here as well.
let passedDayMemo: string | null = null;

export function hasMicPass(now: Date = new Date()): boolean {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(LS_PASSED_DAY);
  } catch {
    // blocked
  }
  return passCurrent(stored, now) || passCurrent(passedDayMemo, now);
}

export function writePassedDay(now: Date): void {
  passedDayMemo = todayKey(now);
  try {
    localStorage.setItem(LS_PASSED_DAY, passedDayMemo);
  } catch {
    // blocked or full
  }
}
